// FUNCTIONALITY-115C — Availability request V2 client (anti-abuse signals).
import type { CatalogV2Product } from "./catalog-v2-types.js";
import type { StoreV2Lang } from "./store-v2-locale.js";
import type { AvailabilityAttachment } from "./availability-attachment-v2.js";

export interface AvailabilityRequestResult {
  ok: boolean;
  eventId?: string;
  managerDeliveryStatus?: string;
  managerMessageId?: string;
  attachmentMessageId?: string;
  duplicate?: boolean;
  challengeRequired?: boolean;
  message: string;
}

function telegramUser(): {
  id: string | null;
  username: string | null;
  firstName: string | null;
} {
  try {
    const user = (
      window as unknown as {
        Telegram?: {
          WebApp?: {
            initDataUnsafe?: {
              user?: { id?: string | number; username?: string; first_name?: string };
            };
          };
        };
      }
    ).Telegram?.WebApp?.initDataUnsafe?.user;
    return {
      id: user?.id == null ? null : String(user.id).slice(0, 32),
      username: user?.username?.slice(0, 64) ?? null,
      firstName: user?.first_name?.slice(0, 80) ?? null,
    };
  } catch {
    return { id: null, username: null, firstName: null };
  }
}

function successMessage(lang: StoreV2Lang): string {
  return lang === "en"
    ? "Your request was sent to the manager ✓\nWe will check availability and contact you."
    : "Запит передано менеджеру ✓\nМи уточнимо наявність і зв’яжемося з вами.";
}

function failMessage(lang: StoreV2Lang, kind: "offline" | "rate" | "challenge" | "generic" = "generic"): string {
  if (kind === "offline") {
    return lang === "en" ? "No connection. Please try again." : "Немає зв’язку. Спробуйте ще раз.";
  }
  if (kind === "rate") {
    return lang === "en"
      ? "Too many requests. Please wait a bit and try again."
      : "Забагато запитів. Зачекайте трохи й спробуйте знову.";
  }
  if (kind === "challenge") {
    return lang === "en"
      ? "Please confirm you are human and try again in a moment."
      : "Підтвердіть, що ви людина, і спробуйте трохи пізніше.";
  }
  return lang === "en"
    ? "Could not send the request. Please try again."
    : "Не вдалося передати запит. Спробуйте ще раз.";
}

export async function requestProductAvailability(input: {
  product: CatalogV2Product;
  displayTitle: string;
  lang: StoreV2Lang;
  sessionKey: string;
  sourceRoute: string;
  customerPhone?: string | null;
  customerComment?: string | null;
  customerName?: string | null;
  priceDisplay?: string | null;
  weight?: string | null;
  attachment?: AvailabilityAttachment | null;
  honeypot?: string | null;
  interactionMs?: number | null;
  idempotencyKey?: string | null;
  challengeToken?: string | null;
}): Promise<AvailabilityRequestResult> {
  const tg = telegramUser();
  const success = successMessage(input.lang);
  const properties = {
    source_surface: "store_v2",
    source_route: input.sourceRoute,
    source_title: input.displayTitle,
    product_id: input.product.id,
    sku: input.product.sku,
    title: input.displayTitle,
    brand: input.product.brand,
    category: input.product.categoryName,
    weight: input.weight ?? input.product.weight,
    price_display: input.priceDisplay ?? input.product.displayPrice,
    price_uah: input.product.priceUah,
    image_url: input.product.imageUrl,
    displayed_availability: input.product.stockStatus,
    username: tg.username,
    customer_name: input.customerName ?? tg.firstName,
    customer_phone: input.customerPhone ?? null,
    customer_comment: input.customerComment ?? null,
    locale: input.lang,
    timestamp: new Date().toISOString(),
    honeypot: input.honeypot ?? "",
    interaction_ms: input.interactionMs ?? null,
    idempotency_key: input.idempotencyKey ?? null,
    challenge_token: input.challengeToken ?? null,
  };

  try {
    let response: Response;
    if (input.attachment) {
      const form = new FormData();
      form.set("session_key", input.sessionKey);
      form.set("event_type", "availability_requested");
      form.set("telegram_user_id", tg.id ?? "");
      form.set("surface", "store_v2");
      form.set("properties_json", JSON.stringify(properties));
      form.set("attachment", input.attachment.file, input.attachment.fileName);
      form.set("attachment_hash", input.attachment.contentHash);
      response = await fetch("/api/store-v2/availability-request", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: form,
      });
    } else {
      response = await fetch("/api/store-v2/event", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          event_type: "availability_requested",
          session_key: input.sessionKey,
          telegram_user_id: tg.id,
          surface: "store_v2",
          properties,
        }),
      });
    }
    const json = (await response.json().catch(() => ({}))) as Record<string, unknown>;
    if (json.error === "availability_rate_limited") {
      return { ok: false, message: failMessage(input.lang, "rate") };
    }
    if (json.error === "availability_challenge_required" || json.challenge_required === true) {
      return { ok: false, challengeRequired: true, message: failMessage(input.lang, "challenge") };
    }
    if (!response.ok || json.ok === false) {
      return { ok: false, message: failMessage(input.lang) };
    }
    return {
      ok: true,
      ...(typeof json.event_id === "string" ? { eventId: json.event_id } : {}),
      ...(typeof json.manager_delivery_status === "string"
        ? { managerDeliveryStatus: json.manager_delivery_status }
        : {}),
      ...(typeof json.manager_message_id === "string"
        ? { managerMessageId: json.manager_message_id }
        : {}),
      ...(typeof json.attachment_message_id === "string"
        ? { attachmentMessageId: json.attachment_message_id }
        : {}),
      duplicate: json.duplicate === true || json.deduped === true,
      message: success,
    };
  } catch {
    return { ok: false, message: failMessage(input.lang, "offline") };
  }
}
